import React, { useState, useRef, useCallback, useEffect } from "react";
import { Button, Form, InputGroup, Badge, Spinner } from "react-bootstrap";
import PropTypes from "prop-types";
import edit from "../../assets/svg/reference_edit.svg";
import del from "../../assets/svg/delete.svg";
import delRed from "../../assets/svg/trash_red.svg";
import saveIcon from "../../assets/svg/tick_white.svg";
import cameraWhiteIcon from "../../assets/svg/cemera_white.svg";
import uploadWhiteIcon from "../../assets/svg/upload_white.svg";
import imgFileIcon from "../../assets/svg/img_file.svg";
import closeRedIcon from "../../assets/svg/close_red.svg";
import addRedIcon from "../../assets/svg/add_red.svg";
import searchIcon from "../../assets/svg/search.svg";
import closeIcon from "../../assets/svg/close.svg";
import CapturePictureModal from "../common/capture-picture";
import EditConfirmModal from "../common/edit-confirm-modal";
import DeleteConfirmModal from "../common/delete-confirm-modal";
import ImagePreviewModal from "../common/image-preview-modal";
import "./reference-section.css";
import { getEnabledFieldApi, createDockReferenceApi, updateDockReferenceApi, deleteDockReferenceApi, addDockReferenceAttachmentsApi, getDockReferenceEmailsApi, viewDockReferenceApi, removeDockReferenceAttachmentsApi } from "./dock-api";
import QRScannerModal from "../common/qr-scanner-modal";

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const INITIAL_REF = {
  reference_number: "",
  emails: [],
  attachments: [],
  isEditing: true,
  isNew: true,
  errors: {},
};

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const ReferenceSection = ({ dockId, dockMasterId, departmentId, activityType, references = [], onUpdate, onRefresh, canEdit = true }) => {
  const formatRefs = (refs) => refs.map((ref) => ({ ...ref, reference_number: ref.reference_no || ref.reference_number || "", emails: (ref.emails || []).map((e) => (typeof e === "object" ? e.email : e)), isEditing: false, isNew: false, errors: {} }));
  const [refList, setRefList] = useState(references?.length > 0 ? formatRefs(references) : []);
  const [emailSearch, setEmailSearch] = useState({});
  const [emailError, setEmailError] = useState({});
  const [emailDropdownOpen, setEmailDropdownOpen] = useState({});
  const [globalEmailSuggestions, setGlobalEmailSuggestions] = useState([]);
  const [carouselIndex, setCarouselIndex] = useState({});
  const [cameraOpen, setCameraOpen] = useState(null);
  const [editModalIndex, setEditModalIndex] = useState(null);
  const [deleteModalIndex, setDeleteModalIndex] = useState(null);
  const fileInputRefs = useRef({});
  const attachScrollRefs = useRef({});
  const [previewImage, setPreviewImage] = useState(null);
  const [previewImages, setPreviewImages] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [qrScanIndex, setQrScanIndex] = useState(null);

  const [enabledFields, setEnabledFields] = useState(["Reference", "Reference No", "Reference Email", "Reference Attachments"]);
  const [mandatoryFields, setMandatoryFields] = useState([]);

  useEffect(() => {
    setRefList(references?.length > 0 ? formatRefs(references) : []);
  }, [references]);

  useEffect(() => {
    if (dockMasterId) {
      const fetchFieldValidation = async () => {
        try {
          const res = await getEnabledFieldApi({ department_id: departmentId || "", dock_id: dockMasterId });
          if (res) {
            setEnabledFields(res.enabled_fields || []);
            setMandatoryFields(res.mandatory_fields || []);
          }
        } catch (err) {
          console.error(err);
        }
      };
      fetchFieldValidation();
    }
  }, [dockMasterId, departmentId]);

  useEffect(() => {
    if (dockMasterId && activityType) {
      fetchEmailSuggestions();
    }
  }, [dockMasterId, activityType]);

  const fetchEmailSuggestions = async () => {
    const params = new URLSearchParams({ dock_id: dockMasterId, activity_type: activityType, term: "" }).toString();
    const res = await getDockReferenceEmailsApi(params);
    if (res?.data?.success) {
      const list = res.data.suggestionlist.map((s) => s.value);
      setGlobalEmailSuggestions(list);
    }
  };

  const scrollToEnd = useCallback((index) => {
    setTimeout(() => {
      const el = attachScrollRefs.current[index];
      if (el) {
        el.scrollLeft = el.scrollWidth;
      }
    }, 100);
  }, []);

  const handleAddReference = () => {
    setRefList([{ ...INITIAL_REF }, ...refList]);
  };

  const handleEditToggle = (index) => {
    const updated = [...refList];
    if (updated[index].savedJustNow) {
      updated[index].savedJustNow = false;
    } else {
      updated[index].isEditing = !updated[index].isEditing;
    }
    setRefList(updated);
  };

  const handleCancelEdit = (index) => {
    const updated = [...refList];
    const ref = updated[index];

    if (!ref.isNew) {
      // Find original data from references prop to revert changes
      const original = references.find((r) => r.id === ref.id);
      if (original) {
        // Re-format just this one item from original data
        updated[index] = {
          ...original,
          reference_number: original.reference_no || original.reference_number || "",
          emails: (original.emails || []).map((e) => (typeof e === "object" ? e.email : e)),
          isEditing: false,
          isNew: false,
          errors: {},
        };
      } else {
        updated[index].isEditing = false;
      }
    } else {
      updated[index].isEditing = false;
    }
    setRefList(updated);
  };

  const handleSaveRef = async (index) => {
    const ref = refList[index];
    let hasError = false;
    let localErrors = {};

    if (enabledFields.includes("Reference No")) {
      if (!ref.reference_number || ref.reference_number.trim() === "") {
        localErrors.reference_no = "Reference Number is required.";
        hasError = true;
      }
    }

    if ((emailSearch[index] || "").trim() !== "") {
      const searchVal = emailSearch[index].trim();
      const currentEmails = (ref.emails || []).map((e) => (typeof e === "object" ? e.email : e));
      const available = (globalEmailSuggestions || []).filter((email) => email && !currentEmails.includes(email));
      const matches = available.filter((email) => email.toLowerCase().includes(searchVal.toLowerCase()));
      const err = matches.length === 0 ? "No email found" : "Please select a Reference Email from the list.";
      setEmailError((prev) => ({ ...prev, [index]: err }));
      hasError = true;
    }

    if (enabledFields.includes("Reference Email") && mandatoryFields.includes("Reference Email")) {
      if (!ref.emails || ref.emails.length === 0) {
        localErrors.emails = "Reference Email is required.";
        hasError = true;
      }
    }

    if (hasError || emailError[index]) {
      setRefList((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], errors: { ...updated[index].errors, ...localErrors } };
        return updated;
      });
      return;
    }

    const selectedEmails = (ref.emails || []).map((e) => (typeof e === "object" ? e.email : e));

    setRefList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], isSaving: true, errors: {} };
      return updated;
    });

    try {
      if (ref.isNew) {
        const payload = {
          dock_id: dockId,
          dock_master_id: dockMasterId,
          reference_no: ref.reference_number,
          activity_type: activityType,
          emails: selectedEmails,
          attachments: [],
        };
        const response = await createDockReferenceApi(payload);
        if (response?.data?.success) {
          const newId = response.data.id || response.data.data?.id;
          if (newId) {
            setRefList((prev) => {
              const updated = [...prev];
              updated[index] = { ...updated[index], isNew: false, id: newId, savedJustNow: true, isSaving: false };
              return updated;
            });
          } else {
            if (onRefresh) onRefresh();
          }
        } else {
          setRefList((prev) => {
            const updated = [...prev];
            const e = response?.data?.error || {};
            updated[index] = {
              ...updated[index],
              isSaving: false,
              errors: {
                reference_no: e.reference_no?.[0],
                activity_type: e.activity_type?.[0],
                dock_id: e.dock_id?.[0],
                attachments: e.attachments?.[0],
                general: response?.data?.message,
              },
            };
            return updated;
          });
        }
      } else {
        const payload = {
          reference_no: ref.reference_number,
          activity_type: ref.activity_type || activityType,
          reason: "Updating reference details",
          emails: selectedEmails,
        };

        if (ref.remove_attachment_ids && ref.remove_attachment_ids.length > 0) {
          payload.remove_attachment_ids = ref.remove_attachment_ids;
        }

        const newAtts = (ref.attachments || []).filter((a) => a.file && !a.uploaded);
        if (newAtts.length > 0) {
          let base64Attachments = [];
          for (let attach of newAtts) base64Attachments.push(await fileToBase64(attach.file));
          await addDockReferenceAttachmentsApi(ref.id, { attachments: base64Attachments });
        }

        const response = await updateDockReferenceApi(ref.id, payload);
        if (response?.data?.success) {
          if (onRefresh) onRefresh();
        } else {
          setRefList((prev) => {
            const updated = [...prev];
            const e = response?.data?.error || {};
            updated[index] = {
              ...updated[index],
              isSaving: false,
              errors: {
                reference_no: e.reference_no?.[0],
                activity_type: e.activity_type?.[0],
                dock_id: e.dock_id?.[0],
                attachments: e.attachments?.[0],
                general: response?.data?.message,
              },
            };
            return updated;
          });
        }
      }
    } catch (e) {
      console.error(e);
      setRefList((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], isSaving: false };
        return updated;
      });
      alert("An error occurred");
    }
  };

  const handleDeleteRef = async (index) => {
    const ref = refList[index];
    if (ref.isNew) {
      const updated = refList.filter((_, i) => i !== index);
      setRefList(updated);
      return;
    }
    setDeleteModalIndex(index);
  };

  const confirmDeleteRef = async (remarks) => {
    if (deleteModalIndex === null) return;
    const ref = refList[deleteModalIndex];
    try {
      const response = await deleteDockReferenceApi(ref.id, { reason: remarks || "Deleted reference" });
      if (response?.data?.success) {
        if (onRefresh) onRefresh();
      } else {
        alert(response?.data?.message || "Error deleting reference");
      }
    } catch (e) {
      console.error(e);
      alert("An error occurred while deleting.");
    } finally {
      setDeleteModalIndex(null);
    }
  };

  const handleRefNumberChange = (index, value) => {
    const updated = [...refList];
    updated[index].reference_number = value;
    if (updated[index].errors?.reference_no) {
      updated[index].errors = { ...updated[index].errors, reference_no: null };
    }
    setRefList(updated);
  };

  const handleRemoveEmail = (refIndex, emailIndex) => {
    const updated = [...refList];
    const newEmails = (updated[refIndex]?.emails || []).filter((_, i) => i !== emailIndex);
    updated[refIndex].emails = newEmails;
    if (enabledFields.includes("Reference Email") && mandatoryFields.includes("Reference Email")) {
      if (newEmails.length === 0) {
        updated[refIndex].errors = { ...updated[refIndex].errors, emails: "Reference Email is required." };
      } else if (updated[refIndex].errors?.emails) {
        const nextErrors = { ...updated[refIndex].errors };
        delete nextErrors.emails;
        updated[refIndex].errors = nextErrors;
      }
    }
    setRefList(updated);
  };

  const getFilteredEmails = (index) => {
    const query = (emailSearch[index] || "").trim().toLowerCase();
    const currentEmails = (refList[index]?.emails || []).map((e) => (typeof e === "object" ? e.email : e));
    const available = (globalEmailSuggestions || []).filter((email) => email && !currentEmails.includes(email));
    if (!query) return available;
    return available.filter((email) => email.toLowerCase().includes(query));
  };

  const selectEmailFromDropdown = (index, email) => {
    const updated = [...refList];
    const currentEmails = (updated[index]?.emails || []).map((e) => (typeof e === "object" ? e.email : e));
    if (!currentEmails.includes(email)) {
      updated[index].emails = [...currentEmails, email];
      if (updated[index].errors?.emails) {
        const nextErrors = { ...updated[index].errors };
        delete nextErrors.emails;
        updated[index].errors = nextErrors;
      }
      setRefList(updated);
    }
    setEmailSearch((prev) => ({ ...prev, [index]: "" }));
    setEmailError((prev) => ({ ...prev, [index]: "" }));
    setEmailDropdownOpen((prev) => ({ ...prev, [index]: false }));
  };

  const handleEmailSearchChange = (index, value) => {
    setEmailSearch((prev) => ({ ...prev, [index]: value }));
    setEmailDropdownOpen((prev) => ({ ...prev, [index]: true }));

    const trimmed = value.trim();
    if (!trimmed) {
      setEmailError((prev) => ({ ...prev, [index]: "" }));
      return;
    }

    const currentEmails = (refList[index]?.emails || []).map((e) => (typeof e === "object" ? e.email : e));
    const available = (globalEmailSuggestions || []).filter((email) => email && !currentEmails.includes(email));
    const matches = available.filter((email) => email.toLowerCase().includes(trimmed.toLowerCase()));

    if (matches.length === 0) {
      setEmailError((prev) => ({ ...prev, [index]: "No email found" }));
    } else {
      setEmailError((prev) => ({ ...prev, [index]: "Please select a Reference Email from the list." }));
    }
  };

  const handleEmailSearchBlur = (index) => {
    setTimeout(() => {
      setEmailDropdownOpen((prev) => ({ ...prev, [index]: false }));
      const trimmed = (emailSearch[index] || "").trim();
      if (trimmed) {
        const currentEmails = (refList[index]?.emails || []).map((e) => (typeof e === "object" ? e.email : e));
        const available = (globalEmailSuggestions || []).filter((email) => email && !currentEmails.includes(email));
        const matches = available.filter((email) => email.toLowerCase().includes(trimmed.toLowerCase()));
        if (matches.length === 0) {
          setEmailError((prev) => ({ ...prev, [index]: "No email found" }));
        } else {
          setEmailError((prev) => ({ ...prev, [index]: "Please select a Reference Email from the list." }));
        }
      } else {
        if (enabledFields.includes("Reference Email") && mandatoryFields.includes("Reference Email")) {
          const currentEmails = refList[index]?.emails || [];
          if (currentEmails.length === 0) {
            setRefList((prev) => {
              const updated = [...prev];
              if (updated[index]) {
                updated[index] = {
                  ...updated[index],
                  errors: { ...updated[index].errors, emails: "Reference Email is required." },
                };
              }
              return updated;
            });
          }
        }
      }
    }, 200);
  };

  const isSaveDisabled = (index) => {
    const ref = refList[index];
    if (!ref) return true;
    if (ref.isSaving) return true;

    // Mandatory Reference No is empty
    if (enabledFields.includes("Reference No")) {
      if (!ref.reference_number || ref.reference_number.trim() === "") return true;
    }

    // emailSearch[index] contains unselected/unmatched text
    if ((emailSearch[index] || "").trim() !== "") return true;

    // Reference Email is mandatory and ref.emails has 0 selected items
    if (enabledFields.includes("Reference Email") && mandatoryFields.includes("Reference Email")) {
      if (!ref.emails || ref.emails.length === 0) return true;
    }

    // Any email or reference number error exists
    if (emailError[index] || ref.errors?.emails || ref.errors?.reference_no) return true;

    return false;
  };

  const handleCameraCapture = async (file) => {
    if (cameraOpen === null) return;
    const index = cameraOpen;
    const ref = refList[index];

    if (ref.isNew) return;

    const newAtt = {
      file: file,
      preview: URL.createObjectURL(file),
      name: file.name,
      isLoading: true,
    };

    setRefList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], attachments: [...(updated[index].attachments || []), newAtt] };
      return updated;
    });
    setCameraOpen(null);
    scrollToEnd(index);

    try {
      const b64 = await fileToBase64(file);
      const response = await addDockReferenceAttachmentsApi(ref.id, b64);

      if (response?.data?.success) {
        const savedId = response.data.id || null;
        setRefList((prev) => {
          const updated = [...prev];
          const atts = [...(updated[index].attachments || [])];
          const attIdx = atts.findIndex((a) => a.preview === newAtt.preview);
          if (attIdx !== -1) {
            atts[attIdx] = { ...atts[attIdx], isLoading: false, uploaded: true, id: savedId };
          }
          updated[index] = { ...updated[index], attachments: atts };
          return updated;
        });
      } else {
        setRefList((prev) => {
          const updated = [...prev];
          const errs = response?.data?.error || {};
          updated[index] = {
            ...updated[index],
            attachments: updated[index].attachments.filter((a) => a.preview !== newAtt.preview),
            errors: {
              ...updated[index].errors,
              attachments: errs.attachment?.[0] || errs.attachments?.[0] || response?.data?.message || "Error uploading attachment",
            },
          };
          return updated;
        });
      }
    } catch (e) {
      alert("An error occurred during upload.");
      setRefList((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], attachments: updated[index].attachments.filter((a) => a.preview !== newAtt.preview) };
        return updated;
      });
    }
  };

  const handleFileUpload = async (index, files) => {
    if (!files || files.length === 0) return;
    const ref = refList[index];

    if (ref.isNew) return;

    const newAttachments = Array.from(files).map((f) => ({
      file: f,
      preview: URL.createObjectURL(f),
      name: f.name,
      isLoading: true,
    }));

    setRefList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], attachments: [...(updated[index].attachments || []), ...newAttachments] };
      return updated;
    });
    scrollToEnd(index);

    try {
      // Upload each file one at a time since the backend accepts single attachment
      for (const attachment of newAttachments) {
        const b64 = await fileToBase64(attachment.file);
        const response = await addDockReferenceAttachmentsApi(ref.id, b64);
        if (response?.data?.success) {
          const savedId = response.data.id || null;
          setRefList((prev) => {
            const updated = [...prev];
            const atts = [...(updated[index].attachments || [])];
            const attIdx = atts.findIndex((a) => a.preview === attachment.preview);
            if (attIdx !== -1) {
              atts[attIdx] = { ...atts[attIdx], isLoading: false, uploaded: true, id: savedId };
            }
            updated[index] = { ...updated[index], attachments: atts };
            return updated;
          });
        } else {
          setRefList((prev) => {
            const updated = [...prev];
            const errs = response?.data?.error || {};
            updated[index] = {
              ...updated[index],
              attachments: updated[index].attachments.filter((a) => a.preview !== attachment.preview),
              errors: {
                ...updated[index].errors,
                attachments: errs.attachment?.[0] || errs.attachments?.[0] || response?.data?.message || `Error uploading file ${attachment.name}`,
              },
            };
            return updated;
          });
        }
      }
    } catch (e) {
      alert("An error occurred during upload.");
      setRefList((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], attachments: updated[index].attachments.filter((a) => !newAttachments.some((na) => na.preview === a.preview)) };
        return updated;
      });
    }
  };

  const handleRemoveAttachment = async (refIndex, attIndex) => {
    const updated = [...refList];
    const removed = updated[refIndex].attachments[attIndex];

    if (removed.id) {
      // Set to loading to show spinner on the remove X button
      setRefList((prev) => {
        const next = [...prev];
        const nextAtts = [...next[refIndex].attachments];
        nextAtts[attIndex] = { ...removed, isRemoving: true };
        next[refIndex] = { ...next[refIndex], attachments: nextAtts };
        return next;
      });

      try {
        await removeDockReferenceAttachmentsApi(updated[refIndex].id, removed.id);
      } catch (err) {
        console.error("Error removing attachment:", err);
      }
    }

    if (removed?.preview) {
      URL.revokeObjectURL(removed.preview);
    }

    setRefList((prev) => {
      const next = [...prev];
      next[refIndex] = {
        ...next[refIndex],
        attachments: next[refIndex].attachments.filter((_, i) => i !== attIndex),
      };
      return next;
    });
  };

  const handleCarouselPrev = (index) => {
    if (attachScrollRefs.current[`view-${index}`]) {
      attachScrollRefs.current[`view-${index}`].scrollBy({ left: -150, behavior: "smooth" });
    }
  };

  const handleCarouselNext = (index) => {
    if (attachScrollRefs.current[`view-${index}`]) {
      attachScrollRefs.current[`view-${index}`].scrollBy({ left: 150, behavior: "smooth" });
    }
  };

  const getAttachmentSrc = (att) => {
    if (att?.preview) return att.preview;
    if (att?.file_url) return att.file_url;
    if (typeof att === "string") return att;
    return "";
  };

  const renderEmailInitial = (email) => {
    return email ? email.charAt(0).toUpperCase() : "?";
  };

  const renderViewMode = (ref, index) => {
    const attachments = ref.attachments || [];

    return (
      <div className="mb-4" key={`ref-view-${index}`}>
        <div className="p-2 mb-2" style={{ borderRadius: "16px", backgroundColor: "#F9F9F9" }}>
          {/* Inner White Card */}
          <div className="p-3 bg-white" style={{ borderRadius: "12px", boxShadow: "0px 2px 4px rgba(0,0,0,0.02)" }}>
            {/* Header row */}
            <div className="d-flex align-items-center justify-content-between mb-3 px-4 py-2" style={{ background: "#FFEFF0", borderRadius: "10px" }}>
              <div className="d-flex align-items-center gap-3 py-1">
                {enabledFields.includes("Reference No") && (
                  <>
                    <span style={{ color: "#172B4D", fontWeight: "500", fontSize: "13px" }}>Reference No. :</span>
                    <span style={{ color: "#D35258", fontWeight: "600", fontSize: "15px" }}>{ref.reference_number || "N/A"}</span>
                  </>
                )}
              </div>
              {canEdit && (
                <div className="d-flex gap-2">
                  <Button className="rounded-circle d-flex align-items-center justify-content-center border-0 p-0" style={{ background: "#FFFFFF", width: "28px", height: "28px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }} onClick={() => setEditModalIndex(index)} title="Edit">
                    <img src={edit} alt="edit" width="14" height="14" style={{ display: "block" }} />
                  </Button>
                  <Button className="rounded-circle d-flex align-items-center justify-content-center border-0 p-0" style={{ background: "#FFFFFF", width: "28px", height: "28px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }} onClick={() => handleDeleteRef(index)} title="Delete">
                    <img src={delRed} alt="delete" width="14" height="14" style={{ display: "block" }} />
                  </Button>
                </div>
              )}
            </div>

            {/* Emails */}
            {enabledFields.includes("Reference Email") && ref.emails?.length > 0 && (
              <div className="mb-3">
                <small className="d-block mb-2 text-uppercase" style={{ fontSize: "11px", fontWeight: "600", color: "#8790A1", letterSpacing: "0.5px" }}>
                  EMAILS ({ref.emails.length})
                </small>
                <div className="d-flex flex-wrap gap-3 ref-emails-scroll">
                  {ref.emails.map((email, eIdx) => (
                    <div className="d-flex align-items-center rounded-pill py-1 px-2 border-0" style={{ background: "#F9F9F9" }} key={`email-${index}-${eIdx}`}>
                      <div className="bg-white rounded d-flex align-items-center justify-content-center me-2" style={{ width: "24px", height: "24px", fontSize: "12px", fontWeight: "600", color: "#172B4D", border: "1px solid #E5E5E5" }}>
                        {renderEmailInitial(email)}
                      </div>
                      <span style={{ color: "#172B4D", fontSize: "12px", fontWeight: "400", paddingRight: "4px" }}>{email}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Divider */}
            {enabledFields.includes("Reference Attachments") && attachments.length > 0 && enabledFields.includes("Reference Email") && ref.emails?.length > 0 && <hr style={{ borderColor: "#E5E5E5", margin: "20px 0" }} />}

            {/* Attachments Grid */}
            {enabledFields.includes("Reference Attachments") && attachments.length > 0 && (
              <div className="d-flex flex-wrap gap-2">
                {attachments.map((att, aIdx) => (
                  <div className="rounded-4 overflow-hidden flex-shrink-0" key={`view-att-${index}-${aIdx}`} style={{ width: "64px", height: "64px", border: "1px solid #E5E5E5" }}>
                    <img 
                      src={getAttachmentSrc(att)} 
                      alt={`attachment-${aIdx}`} 
                      className="w-100 h-100 ref-clickable-thumb" 
                      style={{ objectFit: "cover" }} 
                      onClick={() => {
                        setPreviewImages(attachments.map(a => getAttachmentSrc(a)));
                        setPreviewIndex(aIdx);
                        setPreviewImage(getAttachmentSrc(att));
                      }} 
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Created info - Placed INSIDE the grey wrapper at bottom */}
          {ref.created_date && (
            <div className="px-3 pt-1 pb-0">
              <small style={{ color: "#8790A1", fontSize: "12px", fontWeight: "400" }}>
                Created on {ref.created_date}
                {ref.created_by ? ` by ${ref.created_by}` : ""}
              </small>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderEditMode = (ref, index) => {
    const attachments = ref.attachments || [];

    return (
      <div className="rounded-3 p-3 mb-2" style={{ background: "#F9F9F9" }} key={`ref-edit-${index}`}>
        <div className="p-2 rounded-4 mb-2" style={{ backgroundColor: "#ffffff" }}>
          {/* Reference Number Input */}
          <div className={`d-flex align-items-center gap-2 mb-3 ${!enabledFields.includes("Reference No") ? "justify-content-end" : ""}`}>
            {enabledFields.includes("Reference No") && (
              <div className="ref-float-group flex-grow-1 position-relative">
                <Form.Control type="text" placeholder=" " value={ref.reference_number || ""} onChange={(e) => handleRefNumberChange(index, e.target.value)} disabled={ref.savedJustNow} className="ref-float-input fw-semibold" style={{ height: "40px", border: "1.5px solid #E5E5E5", borderRadius: "8px", background: "#FFFFFF", color: "#172B4D", paddingRight: "44px" }} />
                <label className="ref-float-label">Reference Number *</label>
                {/* QR Scan Button inside input */}
                {/* {!ref.savedJustNow && (
                  <button
                    type="button"
                    title="Scan QR Code"
                    onClick={() => setQrScanIndex(index)}
                    style={{
                      position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)",
                      background: "none", border: "none", padding: "4px", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      zIndex: 3, color: "#D35258",
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="3" width="7" height="7" rx="1" stroke="#D35258" strokeWidth="2"/>
                      <rect x="14" y="3" width="7" height="7" rx="1" stroke="#D35258" strokeWidth="2"/>
                      <rect x="3" y="14" width="7" height="7" rx="1" stroke="#D35258" strokeWidth="2"/>
                      <rect x="5" y="5" width="3" height="3" fill="#D35258"/>
                      <rect x="16" y="5" width="3" height="3" fill="#D35258"/>
                      <rect x="5" y="16" width="3" height="3" fill="#D35258"/>
                      <path d="M14 14h2v2h-2zM18 14h3v2h-3zM14 18h2v3h-2zM18 18h3v3h-3z" fill="#D35258"/>
                    </svg>
                  </button>
                )} */}
              </div>
            )}
            {!ref.savedJustNow ? (
              <>
                {ref.isNew ? (
                  <Button className="ref-icon-btn rounded-circle flex-shrink-0 d-flex align-items-center justify-content-center p-0" style={{ background: "#FFFFFF", border: "1px solid #E5E5E5" }} onClick={() => handleDeleteRef(index)} title="Remove">
                    <img src={closeIcon} alt="remove" width="14" height="14" style={{ display: "block" }} />
                  </Button>
                ) : (
                  <Button className="ref-icon-btn rounded-circle flex-shrink-0 d-flex align-items-center justify-content-center p-0" style={{ background: "#FFFFFF", border: "1px solid #E5E5E5" }} onClick={() => handleCancelEdit(index)} title="Cancel">
                    <img src={closeIcon} alt="cancel" width="14" height="14" style={{ display: "block" }} />
                  </Button>
                )}
                <Button className="ref-icon-btn rounded-circle flex-shrink-0 border-0 d-flex align-items-center justify-content-center p-0" style={{ background: isSaveDisabled(index) ? "#E5E5E5" : "#6FCF97", cursor: isSaveDisabled(index) ? "not-allowed" : "pointer" }} onClick={() => handleSaveRef(index)} title="Save" disabled={isSaveDisabled(index)}>
                  {ref.isSaving ? <Spinner animation="border" variant="light" size="sm" style={{ width: "16px", height: "16px", borderWidth: "2px" }} /> : <img src={saveIcon} alt="save" width="16" height="16" style={{ display: "block" }} />}
                </Button>
              </>
            ) : (
              // When newly saved in this session, show Edit & Delete buttons
              <>
                <Button className="rounded-circle d-flex align-items-center justify-content-center border-0 p-0 flex-shrink-0" style={{ background: "#FFFFFF", width: "36px", height: "36px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }} onClick={() => setEditModalIndex(index)} title="Edit">
                  <img src={edit} alt="edit" width="16" height="16" style={{ display: "block" }} />
                </Button>
                <Button className="rounded-circle d-flex align-items-center justify-content-center border-0 p-0 flex-shrink-0" style={{ background: "#FFFFFF", width: "36px", height: "36px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }} onClick={() => handleDeleteRef(index)} title="Delete">
                  <img src={delRed} alt="delete" width="16" height="16" style={{ display: "block" }} />
                </Button>
              </>
            )}
          </div>
          {ref.errors?.reference_no && (
            <Form.Text className="text-danger d-block px-1 mb-2" style={{ marginTop: "-8px", fontSize: "12px", fontWeight: "500", color: "#eb5757" }}>
              {ref.errors.reference_no}
            </Form.Text>
          )}
          {/* Emails Section */}
          {enabledFields.includes("Reference Email") && (
            <div className="mb-3 mt-3 position-relative">
              <div className={`d-flex align-items-center rounded-4 p-1 ref-search-float-group ${emailError[index] || ref.errors?.emails ? "is-invalid has-error" : ""}`} style={{ background: "#FFFFFF" }}>
                <div className="ps-3 pe-2 d-flex align-items-center opacity-50 z-index-2">
                  <img src={searchIcon} alt="search" width="18" height="18" />
                </div>
                <input
                  type="text"
                  className={`flex-grow-1 border-0 shadow-none bg-transparent ref-float-input ${emailError[index] || ref.errors?.emails ? "is-invalid" : ""}`}
                  placeholder="Search and select emails to add"
                  value={emailSearch[index] || ""}
                  disabled={ref.savedJustNow}
                  onChange={(e) => handleEmailSearchChange(index, e.target.value)}
                  onFocus={() => setEmailDropdownOpen((prev) => ({ ...prev, [index]: true }))}
                  onBlur={() => handleEmailSearchBlur(index)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.preventDefault();
                  }}
                  style={{ fontSize: "14px", color: "#172B4D", outline: "none", height: "40px", zIndex: 3 }}
                />
                <label className="ref-float-label">Emails {mandatoryFields.includes("Reference Email") ? "*" : ""}</label>

                {/* Email Dropdown */}
                {emailDropdownOpen[index] && (getFilteredEmails(index).length > 0 || (emailSearch[index] || "").trim() !== "") && (
                  <div className="ref-email-dropdown position-absolute" style={{ top: "100%", left: "0", right: "0", marginTop: "4px", zIndex: 10 }}>
                    {getFilteredEmails(index).length > 0 ? (
                      getFilteredEmails(index).map((email, dIdx) => (
                        <button
                          type="button"
                          className="ref-email-dropdown-item"
                          key={`dropdown-${index}-${dIdx}`}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            selectEmailFromDropdown(index, email);
                          }}
                        >
                          {email}
                        </button>
                      ))
                    ) : (
                      <div className="ref-email-dropdown-item text-muted" style={{ cursor: "default", pointerEvents: "none", color: "#8790A1" }}>
                        No email found
                      </div>
                    )}
                  </div>
                )}
              </div>
              {(emailError[index] || ref.errors?.emails) && (
                <Form.Text className="text-danger d-block px-1 mt-1" style={{ fontSize: "12px", fontWeight: "500", color: "#eb5757" }}>
                  {emailError[index] || ref.errors?.emails}
                </Form.Text>
              )}

              {/* Added Emails */}
              {ref.emails?.length > 0 && (
                <div className="mt-2">
                  <small className="d-block mb-1" style={{ fontSize: "11px", fontWeight: "600", color: "#8790A1", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    ADDED EMAILS : {ref.emails.length}
                  </small>
                  <div className="d-flex flex-wrap gap-2 ref-emails-scroll">
                    {ref.emails.map((email, eIdx) => (
                      <span className="d-flex align-items-center py-1 ps-3 pe-2 rounded-pill gap-2" style={{ background: "#F5F7F9", color: "#172B4D", fontSize: "13px" }} key={`edit-email-${index}-${eIdx}`}>
                        {typeof email === "object" ? email.email : email}
                        {!ref.savedJustNow && (
                          <button className="d-flex align-items-center justify-content-center border-0 p-0 rounded-circle" style={{ background: "#E5E5E5", width: "18px", height: "18px", color: "#172B4D", fontSize: "14px", lineHeight: 1 }} onClick={() => handleRemoveEmail(index, eIdx)} title="Remove email">
                            <img src={closeIcon} alt="remove" width="10" height="10" style={{ display: "block" }} />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {ref.errors?.attachments && (
            <Form.Text className="text-danger d-block px-3 mt-2" style={{ fontSize: "12px", fontWeight: "500" }}>
              {ref.errors.attachments}
            </Form.Text>
          )}
        </div>

        {/* General/Other Save Errors area */}
        {ref.errors?.general && ref.errors?.general !== "Validation error" && <Form.Text className="text-danger d-block px-3 mt-1 fw-semibold">{ref.errors.general}</Form.Text>}
        {ref.errors?.dock_id && <Form.Text className="text-danger d-block px-3 mt-1">{ref.errors.dock_id}</Form.Text>}
        {ref.errors?.activity_type && <Form.Text className="text-danger d-block px-3 mt-1">{ref.errors.activity_type}</Form.Text>}

        {/* Attachments Section */}
        {enabledFields.includes("Reference Attachments") && !ref.isNew && (
          <div className="mt-2">
            <small className="fw-semibold d-block mb-2" style={{ color: "#172B4D" }}>
              Attachments {mandatoryFields.includes("Reference Attachments") ? "*" : ""}
            </small>

            {attachments.length === 0 ? (
              /* Empty state - Full width Drag & Drop */
              <div className="d-flex align-items-center gap-3 rounded-4 p-4" style={{ background: "#FFFF", border: "1.5px dashed #E5E5E5" }}>
                <div className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0" style={{ width: "56px", height: "56px", background: "#FDEAEA" }}>
                  <img src={imgFileIcon} alt="add" width="32" height="32" />
                </div>
                <div className="flex-grow-1">
                  <span style={{ fontSize: "14px", color: "#172B4D", fontWeight: "600", display: "block" }}>Select images to add</span>
                  <small style={{ fontSize: "12px", color: "#8790A1" }}>Drag & Drop or select from your computer</small>
                </div>
                <div className="d-flex gap-2 flex-shrink-0">
                  <Button className="d-flex align-items-center gap-2 rounded-pill border-0 px-3 py-2 ref-btn-primary-red" style={{ fontSize: "13px", fontWeight: "600" }} onClick={() => setCameraOpen(index)}>
                    <img src={cameraWhiteIcon} alt="camera" width="16" height="16" />
                    Camera
                  </Button>
                  <Button className="d-flex align-items-center gap-2 rounded-pill px-3 py-2" style={{ background: "#FFFFFF", border: "1px solid #E5E5E5", color: "#172B4D", fontSize: "13px", fontWeight: "600" }} onClick={() => fileInputRefs.current[index]?.click()}>
                    <img src={uploadWhiteIcon} alt="upload" width="16" height="16" />
                    Upload
                  </Button>
                </div>
                <input ref={(el) => (fileInputRefs.current[index] = el)} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={(e) => handleFileUpload(index, e.target.files)} />
              </div>
            ) : (
              /* Has attachments - Small thumbnail grid */
              <div className="d-flex gap-2 align-items-start ref-attachments-scroll" ref={(el) => (attachScrollRefs.current[index] = el)}>
                {attachments.map((att, aIdx) => (
                  <div className="ref-attachment-thumb position-relative rounded-3 overflow-hidden flex-shrink-0" key={`att-${index}-${aIdx}`}>
                    <img 
                      src={getAttachmentSrc(att)} 
                      alt={`attachment-${aIdx}`} 
                      className="w-100 h-100 ref-clickable-thumb" 
                      style={{ objectFit: "cover", opacity: att.isRemoving ? 0.6 : 1 }} 
                      onClick={() => {
                        setPreviewImages(attachments.map(a => getAttachmentSrc(a)));
                        setPreviewIndex(aIdx);
                        setPreviewImage(getAttachmentSrc(att));
                      }} 
                    />

                    {att.isLoading && (
                      <div className="position-absolute bottom-0 start-0 w-100 overflow-hidden rounded-bottom-3" style={{ height: "4px", background: "rgba(255,255,255,0.4)" }}>
                        <div className="ref-loading-bar-custom"></div>
                      </div>
                    )}

                    {!att.isLoading && (
                      <Button size="sm" className="ref-attachment-remove position-absolute rounded-circle p-0 border-0 d-flex align-items-center justify-content-center" style={{ background: "#FFE2E3", width: "20px", height: "20px", top: "4px", right: "4px" }} onClick={() => handleRemoveAttachment(index, aIdx)} disabled={att.isRemoving}>
                        {att.isRemoving ? <Spinner animation="border" variant="danger" size="sm" style={{ width: "10px", height: "10px", borderWidth: "1.5px" }} /> : <img src={closeRedIcon} alt="remove" width="10" height="10" style={{ display: "block" }} />}
                      </Button>
                    )}
                  </div>
                ))}

                {/* Add image button - sticky right */}
                <div 
                  className="ref-add-attachment-card d-flex flex-column align-items-center justify-content-center px-3 py-3 rounded-3 flex-shrink-0 ref-add-card-sticky" 
                  style={{ minHeight: "100px", width: "180px", gap: "10px", borderStyle: "dashed" }}
                >
                  <img src={imgFileIcon} alt="add" width="28" height="28" className="opacity-50" />
                  <div className="d-flex gap-2 w-100 align-items-center justify-content-center">
                    <Button 
                      size="sm" 
                      className="rounded-pill border-0 d-flex align-items-center justify-content-center gap-1 py-1 px-2 flex-fill ref-btn-primary-red" 
                      style={{ fontSize: "10px", fontWeight: "700" }} 
                      onClick={() => setCameraOpen(index)}
                    >
                      <img src={cameraWhiteIcon} alt="camera" width="12" height="12" />
                      CAMERA
                    </Button>
                    <Button 
                      size="sm" 
                      className="rounded-pill d-flex align-items-center justify-content-center gap-1 py-1 px-2 flex-fill" 
                      style={{ background: "#FFFFFF", border: "1px solid #E5E5E5", fontSize: "10px", fontWeight: "700", color: "#172B4D" }} 
                      onClick={() => fileInputRefs.current[index]?.click()}
                    >
                      <img src={uploadWhiteIcon} alt="upload" width="12" height="12" />
                      UPLOAD
                    </Button>
                  </div>
                  <input ref={(el) => (fileInputRefs.current[index] = el)} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={(e) => handleFileUpload(index, e.target.files)} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (!enabledFields.includes("Reference") && !enabledFields.includes("Reference No")) {
    return null;
  }

  return (
    <div className="mt-3" style={{ fontFamily: "Epilogue" }}>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h3 className="m-0" style={{ fontSize: "16px", fontWeight: "700", color: "#172B4D" }}>Reference & Attachments</h3>
        {canEdit && (
          <button className="ref-add-ref-btn d-flex align-items-center gap-2" onClick={handleAddReference}>
            <img src={addRedIcon} alt="add" width="12" height="12" />
            <span>Add Reference</span>
          </button>
        )}
      </div>

      {refList.map((ref, index) => (
        <div key={`ref-block-${index}`}>
          {ref.isEditing || ref.isNew ? renderEditMode(ref, index) : renderViewMode(ref, index)}
          {index < refList.length - 1 && <hr />}
        </div>
      ))}



      {cameraOpen !== null && (
        <CapturePictureModal
          onSuccess={(base64Img) => {
            let arr = base64Img.split(","),
              mime = arr[0].match(/:(.*?);/)[1],
              bstr = atob(arr[1]),
              n = bstr.length,
              u8arr = new Uint8Array(n);
            while (n--) {
              u8arr[n] = bstr.charCodeAt(n);
            }
            const file = new File([u8arr], `camera-${Date.now()}.png`, { type: mime });
            handleCameraCapture(file);
          }}
          onCancel={() => setCameraOpen(null)}
        />
      )}
      {editModalIndex !== null && (
        <EditConfirmModal
          onConfirm={() => {
            handleEditToggle(editModalIndex);
            setEditModalIndex(null);
          }}
          onCancel={() => setEditModalIndex(null)}
        />
      )}

      {deleteModalIndex !== null && <DeleteConfirmModal onConfirm={confirmDeleteRef} onCancel={() => setDeleteModalIndex(null)} />}

      {/* QR Scanner Modal */}
      {qrScanIndex !== null && (
        <QRScannerModal
          onSuccess={(qrText) => {
            handleRefNumberChange(qrScanIndex, qrText);
            setQrScanIndex(null);
          }}
          onCancel={() => setQrScanIndex(null)}
        />
      )}

      <ImagePreviewModal
        show={previewImage !== null}
        images={previewImages}
        initialIndex={previewIndex}
        onHide={() => setPreviewImage(null)}
        title="Attachment Preview"
      />
    </div>
  );
};

ReferenceSection.propTypes = {
  dockId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dockMasterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  activityType: PropTypes.string,
  references: PropTypes.array,
  onUpdate: PropTypes.func,
  onRefresh: PropTypes.func,
  canEdit: PropTypes.bool,
};

export default ReferenceSection;
